import { Box, CustomerDrawer } from '@components';
import { Table, Button, Input, Icon, Alert } from 'antd';
import { getCustomers } from 'api/customersApi';
import { useQuery } from 'api-hooks/hooks';
import React, { useState } from 'react';
import { getConfig, setConfig } from 'utils/config';
import './CustomerList.less';

type Props = {
  onSelect?: (id: number) => any;
};

const CustomerList: React.FC<Props> = ({ onSelect = () => {} }) => {
  const [lims, setLims] = useState();
  const [name, setName] = useState();
  const [page, setPage] = useState({ current: 1, size: getConfig('pageSize') });
  const [drawer, setDrawer] = useState<{ visible: boolean; customer: any }>({
    visible: false,
    customer: undefined
  });
  const { data: customers, loading, error } = useQuery(
    getCustomers(page.current, page.size, {
      lims__startswith: lims,
      name__icontains: name
    }),
    { skip: !(lims || name) }
  );

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={'Search'}
          value={selectedKeys[0]}
          onChange={e =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon="search"
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Search
        </Button>
        <Button
          onClick={() => handleReset(dataIndex, clearFilters)}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    )
  });

  const columns = [
    {
      title: <>LIMS&nbsp;ID</>,
      dataIndex: 'lims',
      ...getColumnSearchProps('lims')
    },
    {
      title: 'Customer Name',
      dataIndex: 'name',
      ...getColumnSearchProps('name')
    },
    { title: 'Address', dataIndex: 'address' },
    { title: 'City', dataIndex: 'city' },
    {
      title: 'Action',
      key: 'actions',
      render: (v, row, index) => (
        <span className="font-normal">
          <span
            className="text-secondary cursor-pointer"
            onClick={() => openDrawer(index)}
          >
            View
          </span>
          &nbsp;|&nbsp;
          <span
            className="text-primary cursor-pointer"
            onClick={() => onSelect(row.id)}
          >
            Select
          </span>
        </span>
      )
    }
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    setPage({ current: pagination.current, size: pagination.pageSize });
    setConfig('pageSize', pagination.pageSize);
  };

  const openDrawer = (index: number) =>
    setDrawer({ customer: customers?.results[index], visible: true });

  const onCloseDrawer = (id?: number) => {
    setDrawer(d => ({ ...d, visible: false }));
    if (id) {
      onSelect(id);
    }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    if (dataIndex === 'lims') {
      setLims(selectedKeys[0]);
    } else if (dataIndex === 'name') {
      setName(selectedKeys[0]);
    }
    confirm();
  };
  const handleReset = (dataIndex, confirm) => {
    if (dataIndex === 'lims') {
      setLims(undefined);
    } else if (dataIndex === 'name') {
      setName(undefined);
    }
    confirm();
  };

  return (
    <>
      <Box
        header={
          <div className="Customer-List-header">
            <div>Customer Selection</div>
            <div className="subtitle">
              To search for a customer, click the icon&nbsp;
              <Icon type="filter" theme="filled" />
              &nbsp;and type <b>LIMS ID</b> or the <b>Customer Name</b>
            </div>
          </div>
        }
        content={
          <>
            {error && (
              <Alert
                type="error"
                message="Error"
                showIcon
                description="Cannot load data"
              />
            )}
            <Table
              bordered={true}
              columns={columns}
              dataSource={customers?.results}
              rowKey="id"
              loading={loading}
              pagination={{
                current: page.current,
                total: customers?.count,
                pageSize: page.size,
                showSizeChanger: true
              }}
              onChange={handleTableChange}
              locale={{
                emptyText: name || lims ? 'Nothing found' : 'Enter filters'
              }}
            />
          </>
        }
      />
      <CustomerDrawer
        visible={drawer.visible}
        onClose={onCloseDrawer}
        customer={drawer.customer}
      />
    </>
  );
};

export default CustomerList;
